import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from "react";
import "./advancedTimelineSlider.css";
import { Button } from "../button";
const NOW_BUTTON_WIDTH_DEFAULT_PX = 108;
const resolveSliderOptions = (options) => {
    var _a, _b, _c;
    return ({
        compact12HourTickLabels: (_a = options === null || options === void 0 ? void 0 : options.compact12HourTickLabels) !== null && _a !== void 0 ? _a : false,
        nowButtonWidthPx: (_b = options === null || options === void 0 ? void 0 : options.nowButtonWidthPx) !== null && _b !== void 0 ? _b : NOW_BUTTON_WIDTH_DEFAULT_PX,
        showHoverTooltip: (_c = options === null || options === void 0 ? void 0 : options.showHoverTooltip) !== null && _c !== void 0 ? _c : true,
    });
};
/** @internal Exported for unit tests. */
export const formatTimelineLabel = (formatter, timestamp, timeFormat, compact12HourTickLabels) => {
    const formatted = formatter.format(new Date(timestamp)).replace(/,/g, "");
    if (timeFormat !== "12h" || !compact12HourTickLabels) {
        return formatted;
    }
    return formatted.replace(/\s?(AM|PM)$/i, "").trim();
};
/** @internal Exported for unit tests. */
export { resolveSliderOptions };
const createFormatters = (locale, timezone, timeFormat = "12h") => {
    const hourCycle = timeFormat === "24h" ? "h23" : "h12";
    const hour = timeFormat === "24h" ? "2-digit" : "numeric";
    const timeFormatter = new Intl.DateTimeFormat(locale, {
        hour,
        hourCycle,
        minute: "2-digit",
        hour12: timeFormat !== "24h",
        timeZone: timezone,
    });
    const dayTimeFormatter = new Intl.DateTimeFormat(locale, {
        day: "numeric",
        hour,
        hourCycle,
        minute: "2-digit",
        hour12: timeFormat !== "24h",
        timeZone: timezone,
    });
    const dayFormatter = new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "numeric",
        timeZone: timezone,
    });
    return {
        "[13579] 00:00$": dayFormatter,
        "00:00$": dayTimeFormatter,
        "((00)|(12)):00$": timeFormatter,
        "((0[0369])|(1[258])|(21)):00$": timeFormatter,
        ":00$": timeFormatter,
        ":[03]0$": timeFormatter,
        ":[012345]0$": timeFormatter,
    };
};
const createLabels = (timestamps, patternFormatters, locale, timeZone, timeFormat, compact12HourTickLabels) => {
    // Always use 24h for pattern matching; $-anchored regexes expect no AM/PM suffix.
    const testFormatter = new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        hour: "2-digit",
        hourCycle: "h23",
        minute: "2-digit",
        timeZone,
    });
    const labels = timestamps.map((t) => testFormatter.format(new Date(t)).replace(/,/g, ""));
    const patterns = Object.keys(patternFormatters).map((p) => new RegExp(p));
    const max = 8; // could be based on DOM width
    let pattern = patterns[0];
    for (let i = 1; i < patterns.length; i += 1) {
        const matching = labels.filter((l) => l.match(patterns[i]) != null);
        if (matching.length > max) {
            break;
        }
        pattern = patterns[i];
    }
    const displayLabels = labels.map((label, i) => {
        if (label.match(pattern) == null) {
            return "";
        }
        return formatTimelineLabel(patternFormatters[pattern.source], timestamps[i], timeFormat, compact12HourTickLabels);
    });
    return displayLabels;
};
export const AdvancedTimelineSlider = ({ id, testId, value = 0, onChange, timestamps = [], defaultTimestamp, label, locale = "en-US", timezone = "UTC", timeFormat = "12h", sliderOptions, }) => {
    const { compact12HourTickLabels, nowButtonWidthPx, showHoverTooltip } = resolveSliderOptions(sliderOptions);
    const sliderRef = useRef(null);
    const [hovered, setHovered] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0, frac: 0 });
    const handleMouseMove = (e) => {
        var _a;
        const bounds = e.currentTarget.getBoundingClientRect();
        const sliderBounds = (_a = sliderRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!sliderBounds) {
            return;
        }
        const x = e.clientX - sliderBounds.left;
        const frac = (e.clientX - 9 - bounds.x) / (bounds.width - 18); // 9 is radius of thumb
        const y = bounds.top - sliderBounds.top - 24;
        setPosition({ x, y, frac });
    };
    const displayTimestamps = useMemo(() => timestamps.slice(0, -1), [timestamps]);
    const defaultIndex = useMemo(() => timestamps.indexOf(defaultTimestamp), [timestamps, defaultTimestamp]);
    // style display boxes based on left of value, or observed or forecast
    const boxes = useMemo(() => {
        return displayTimestamps.map((t, i) => {
            let style = "m-px border h-[8px]";
            if (i < value) {
                // left
                style += " border-blue-600 bg-blue-600";
            }
            else if (i < defaultIndex) {
                // observed
                style += " border-gray-600 bg-gray-600";
            }
            else {
                // forecast
                style += " border-gray-600";
            }
            if (i === 0) {
                style += " rounded-l-md";
            }
            else if (i === displayTimestamps.length - 1) {
                style += " rounded-r-md";
            }
            return style;
        });
    }, [displayTimestamps, value, defaultIndex]);
    const formatters = useMemo(() => createFormatters(locale, timezone, timeFormat), [locale, timezone, timeFormat]);
    const labels = useMemo(() => createLabels(displayTimestamps, formatters, locale, timezone, timeFormat, compact12HourTickLabels), [
        displayTimestamps,
        formatters,
        locale,
        timezone,
        timeFormat,
        compact12HourTickLabels,
    ]);
    const nowButtonCenterOffsetPx = nowButtonWidthPx / 2;
    const displayObservationsHint = defaultIndex > 0 && defaultIndex < timestamps.length - 1;
    const nowFrac = (defaultTimestamp - timestamps[0]) /
        (timestamps[timestamps.length - 1] - timestamps[0]);
    const nowL = (nowFrac * 100).toFixed(2);
    const nowR = ((1 - nowFrac) * 100).toFixed(2);
    const displayFormatter = formatters[":[012345]0$"];
    const hoverTime = timestamps[0] +
        position.frac *
            (timestamps[timestamps.length - 1] -
                timestamps[0]);
    return (_jsxs("div", { ref: sliderRef, id: id, "data-test-id": testId, "data-eco-component": "eco-advanced-timeline-slider", className: "relative w-full text-xs", children: [_jsxs("div", { className: "w-full", children: [_jsx("div", { className: "relative ml-[8px] mr-[8px] text-nowrap", children: _jsx("div", { className: "mx-[2px] mb-1 inline-block overflow-visible font-bold", children: displayObservationsHint && nowFrac > 0.1 ? (_jsx("span", { children: "Observations" })) : (_jsx("span", { children: "\u00A0" })) }) }), _jsx("div", { className: "ml-[8px] mr-[8px] flex", children: boxes.map((e, i) => (_jsx("div", { className: "w-0 min-w-0 flex-1", children: _jsx("div", { className: e }) }, `box-${i}`))) }), _jsx("div", { className: "ml-[8px] mr-[8px] flex min-w-0 overflow-hidden", children: labels.map((label, i) => (_jsx("div", { className: `${label ? "border-l border-l-gray-300" : ""}
              mt-1 w-0 flex-1 overflow-visible whitespace-nowrap`, children: label && (_jsx("div", { className: "overflow-visible whitespace-nowrap pl-1", children: label })) }, `bottom-label-${i}`))) })] }), _jsx("input", { type: "range", value: value, onChange: (e) => onChange(e.target.valueAsNumber), min: 0, max: timestamps.length - 1, step: "1", onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), onMouseMove: handleMouseMove, className: "absolute inset-0 w-full cursor-pointer" }), _jsx("div", { className: "absolute -top-[6px] mb-1 inline-block overflow-visible text-center", style: nowFrac <= 0.1
                    ? {
                        left: `${nowL}%`,
                    }
                    : nowFrac >= 0.9
                        ? {
                            right: `${nowR}%`,
                        }
                        : {
                            width: `${nowButtonWidthPx}px`,
                            left: `calc(${nowL}% - ${nowButtonCenterOffsetPx}px)`,
                        }, children: 0 <= nowFrac && nowFrac <= 1 && (_jsxs(Button, { id: "now-button", testId: "now-button", variant: "secondary", disabled: value === defaultIndex, size: "xs", onClick: () => onChange(defaultIndex), children: ["NOW - ", displayFormatter.format(defaultTimestamp)] })) }), showHoverTooltip &&
                hovered &&
                hoverTime >= timestamps[0] &&
                hoverTime <= timestamps[timestamps.length - 1] && (_jsxs("div", { className: "absolute flex flex-col items-center", style: {
                    top: `${position.y}px`,
                    left: `${position.x}px`,
                    transform: "translateX(-50%)",
                    pointerEvents: "none",
                }, children: [_jsx("div", { className: "rounded-md bg-black px-4 py-2 text-gray-300", children: displayFormatter.format(new Date(hoverTime)) }), _jsx("div", { className: "", children: _jsx("svg", { width: "12", height: "6", viewBox: "0 0 12 6", fill: "black", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M0 0 h 12 L 6 6 z" }) }) })] })), _jsx("span", { className: "sr-only", children: label })] }));
};
